import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import {
  Observable,
  combineLatest,
  filter,
  map,
  shareReplay,
  startWith,
} from 'rxjs';
import { AdminGatewayService } from 'src/app/gateway/admin-gateway.service';
import {
  AdminAction,
  AdminSection,
  hasPermission,
} from 'src/app/models/admin-permission';
import { LoggedInUser } from 'src/app/models/logged-in-user.model';
import { UserLoggedIn, UserLoggedOut } from 'src/app/state/user/user.action';
import { UserState } from 'src/app/state/user/user.state';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent {
  @Select(UserState.user)
  user$!: Observable<LoggedInUser | null>;

  groups$: Observable<Group[]>;

  private groups: Group[] = [
    {
      active: false,
      icon: 'content_copy',
      text: 'Content',
      routes: [
        {
          key: 'category',
          icon: 'category',
          text: 'Categories',
          url: 'categories',
          section: 'categories',
          active: false,
          allowForScopped: true,
        },
        {
          key: 'question',
          icon: 'quiz',
          text: 'Questions',
          url: 'questions',
          section: 'questions',
          active: false,
          allowForScopped: true,
        },
        {
          key: 'golden-points',
          icon: 'lightbulb',
          text: 'Golden Points',
          url: 'golden-points',
          section: 'golden_points',
          active: false,
          allowForScopped: true,
        },
        {
          key: 'favorite-lists',
          icon: 'favorite',
          text: 'Favorite List',
          url: 'favorite-lists',
          section: 'favorite_lists',
          active: false,
          allowForScopped: true,
        },
        {
          key: 'user-report',
          icon: 'flag',
          text: 'User Reports',
          url: 'user-reports',
          section: 'reports',
          active: false,
          allowForScopped: false,
        },
        {
          key: 'question-report',
          icon: 'flag',
          text: 'Question Reports',
          url: 'question-reports',
          section: 'reports',
          active: false,
          allowForScopped: true,
        },
      ],
    },
    {
      icon: 'group',
      active: false,
      text: 'Users Management',
      routes: [
        {
          active: false,
          icon: 'person',
          key: 'users',
          text: 'Users',
          url: 'users',
          section: 'users',
          allowForScopped: false,
        },
        {
          active: false,
          icon: 'admin_panel_settings',
          key: 'admins',
          text: 'Admins',
          url: 'admins',
          superAdminOnly: true,
          allowForScopped: false,
        },
        {
          active: false,
          icon: 'workspaces',
          key: 'packages',
          text: 'Packages',
          url: 'packages',
          section: 'packages',
          allowForScopped: false,
        },
        {
          active: false,
          icon: 'vpn_key',
          key: 'codes',
          text: 'Codes',
          url: 'codes',
          section: 'codes',
          allowForScopped: false,
        },
      ],
    },
    {
      active: false,
      icon: 'campaign',
      text: 'Engagement',
      routes: [
        {
          key: 'notifications',
          icon: 'notifications',
          text: 'Notifications',
          url: 'notifications',
          section: 'notifications',
          action: 'write',
          active: false,
          allowForScopped: false,
        },
      ],
    },
    {
      active: false,
      icon: 'phone_iphone',
      text: 'App',
      routes: [
        {
          key: 'app-content',
          icon: 'info',
          text: 'App Content',
          url: 'app-content',
          section: 'app_content',
          active: false,
          allowForScopped: false,
        },
        {
          key: 'faqs',
          icon: 'quiz',
          text: 'FAQ',
          url: 'faqs',
          section: 'app_content',
          active: false,
          allowForScopped: false,
        },
      ],
    },
  ];

  private activeLink$: Observable<string | null>;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly location: Location,
    private readonly adminGateway: AdminGatewayService
  ) {
    this.activeLink$ = router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.url),
      startWith(this.router.url),
      map((url) => {
        const baseUrl = url.split('?')[0];
        const routes = this.groups.flatMap((group) => group.routes);
        const route =
          // first try to match last piece
          routes.find((route) => baseUrl.endsWith(route.url))?.key ??
          // then if no match was found try to match any piece of the url
          routes.find((route) => baseUrl.includes(`/${route.url}/`))?.key ??
          null;

        return route;
      })
    );

    this.groups$ = this.buildRoutesStream(this.activeLink$, this.user$);
    this.refreshUser();
  }

  trackByKey(_: any, route: Route) {
    return route.key;
  }

  onLogout() {
    this.store.dispatch(new UserLoggedOut());
    this.router.navigate(['/auth/signin']);
  }

  onBack() {
    this.location.back();
  }

  /** permissions can change while logged in, keep the stored admin up to date */
  private refreshUser() {
    this.adminGateway.me().subscribe({
      next: (response) => {
        const token = this.store.selectSnapshot(UserState.token);
        if (token) {
          this.store.dispatch(new UserLoggedIn({ ...response.data, token }));
        }
      },
    });
  }

  private canSee(route: Route, user: LoggedInUser | null): boolean {
    if (route.superAdminOnly) {
      return user?.role === 'SUPER_ADMIN';
    }
    if (UserState.isScopped(user) && !route.allowForScopped) {
      return false;
    }
    return hasPermission(user, route.section!, route.action ?? 'read');
  }

  private buildRoutesStream(
    activeLinkStream: Observable<string | null>,
    userStream: Observable<LoggedInUser | null>
  ): Observable<Group[]> {
    return combineLatest([activeLinkStream, userStream]).pipe(
      map(([activeLink, user]) =>
        this.groups
          .map((group) => {
            const routes = group.routes
              .map((route) => ({
                ...route,
                active: activeLink === route.key,
              }))
              .filter((route) => this.canSee(route, user));

            return {
              ...group,
              active: routes.some((route) => route.active),
              routes: routes,
            };
          })
          .filter((group) => group.routes.length > 0)
      ),
      shareReplay(1)
    );
  }
}

interface Group {
  active: boolean;
  icon: string;
  text: string;
  routes: Route[];
}

interface Route {
  key: string;
  icon: string;
  url: string;
  text: string;
  active: boolean;
  allowForScopped: boolean;
  section?: AdminSection;
  action?: AdminAction;
  superAdminOnly?: boolean;
}
